import { useState, useEffect } from 'react';
import { Truck, Package, Globe, MapPin, Settings, RefreshCw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminShipping = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testPincode, setTestPincode] = useState('110001');
  const [testCountry, setTestCountry] = useState('India');
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/shipping/config`);
      setConfig(response.data);
    } catch (error) {
      toast.error('Failed to load shipping config');
    } finally {
      setLoading(false);
    }
  };

  const testShippingRates = async () => {
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/shipping/calculate-rates`, {
        delivery_pincode: testPincode,
        delivery_country: testCountry,
        weight: 0.5
      });
      setTestResult(response.data);
      toast.success('Rates calculated successfully');
    } catch (error) {
      toast.error('Failed to calculate rates');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-100 animate-pulse rounded" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-shipping">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Shipping Configuration</h1>
          <p className="text-muted-foreground">Manage shipping partners and rates</p>
        </div>
        <Button variant="outline" onClick={fetchConfig} className="rounded-none">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pickup Location */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5" />
              Pickup Location
            </CardTitle>
            <CardDescription>Where shipping partners pick up orders</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>
                <p className="font-medium">{config?.pickup_location?.name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Phone:</span>
                <p className="font-medium">{config?.pickup_location?.phone}</p>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Address:</span>
                <p className="font-medium">{config?.pickup_location?.address}</p>
              </div>
              <div>
                <span className="text-muted-foreground">City:</span>
                <p className="font-medium">{config?.pickup_location?.city}</p>
              </div>
              <div>
                <span className="text-muted-foreground">State:</span>
                <p className="font-medium">{config?.pickup_location?.state}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Pincode:</span>
                <p className="font-medium">{config?.pickup_location?.pincode}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              To update pickup location, edit the .env file on the server.
            </p>
          </CardContent>
        </Card>

        {/* Margin Settings */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="w-5 h-5" />
              Margin Settings
            </CardTitle>
            <CardDescription>Profit margin applied to shipping costs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="text-5xl font-light text-primary">{config?.margin_percent}%</div>
              <p className="text-muted-foreground mt-2">Margin on all shipping rates</p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 text-sm">
              <p>Example: If carrier charges ₹100, customer pays ₹{100 * (1 + (config?.margin_percent || 10) / 100)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Shiprocket Status */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="w-5 h-5" />
              Shiprocket Integration
            </CardTitle>
            <CardDescription>Domestic shipping partner for India</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`flex items-center gap-2 p-3 rounded ${config?.shiprocket_configured ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {config?.shiprocket_configured ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>Connected</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span>Not configured - using fallback rates</span>
                </>
              )}
            </div>
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Domestic Partners:</p>
              <div className="flex flex-wrap gap-2">
                {config?.domestic_partners?.map(partner => (
                  <span key={partner} className="px-2 py-1 bg-gray-100 text-xs">{partner}</span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* International Shipping */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5" />
              International Shipping
            </CardTitle>
            <CardDescription>Global delivery partners</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm font-medium mb-2">International Partners:</p>
              <div className="flex flex-wrap gap-2">
                {config?.international_partners?.map(partner => (
                  <span key={partner} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs">{partner}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Supported Countries:</p>
              <div className="flex flex-wrap gap-2">
                {config?.supported_countries?.map(country => (
                  <span key={country} className="px-2 py-1 bg-gray-100 text-xs">{country}</span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Shipping Rates */}
      <Card className="rounded-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5" />
            Test Shipping Rates
          </CardTitle>
          <CardDescription>Calculate rates for a test destination</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <Label className="text-sm mb-2 block">Delivery Pincode</Label>
              <Input
                value={testPincode}
                onChange={(e) => setTestPincode(e.target.value)}
                placeholder="110001"
                className="rounded-none"
              />
            </div>
            <div className="flex-1">
              <Label className="text-sm mb-2 block">Country</Label>
              <select
                value={testCountry}
                onChange={(e) => setTestCountry(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200"
              >
                {config?.supported_countries?.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={testShippingRates} className="rounded-none">
                Calculate Rates
              </Button>
            </div>
          </div>

          {testResult && (
            <div className="border border-gray-200">
              <div className="p-4 bg-gray-50 border-b">
                <p className="text-sm">
                  <strong>From:</strong> {testResult.pickup_location?.city}, {testResult.pickup_location?.pincode} → 
                  <strong> To:</strong> {testResult.delivery_country} ({testResult.delivery_pincode})
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Weight: {testResult.weight_kg}kg • Margin: {testResult.margin_percent}%
                </p>
              </div>
              <div className="divide-y">
                {testResult.rates?.map((rate, idx) => (
                  <div key={idx} className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{rate.partner}</p>
                      <p className="text-sm text-muted-foreground">
                        {rate.service} • {rate.estimated_days} days
                        {rate.is_cod_available && <span className="ml-2 text-green-600">COD</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">₹{rate.total_cost}</p>
                      <p className="text-xs text-muted-foreground">
                        Base: ₹{rate.base_cost} + Margin: ₹{rate.margin}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {testResult.duties_note && (
                <div className="p-4 bg-amber-50 text-amber-700 text-sm">
                  {testResult.duties_note}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminShipping;
